import { Form } from "@inertiajs/react"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import {
  RejectionReasonPicker,
  isNoteMissing,
  isNoteRequired,
} from "@/components/candidates/rejection-reason-picker"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer"
import { useIsMobile } from "@/hooks/use-mobile"
import { useOnline } from "@/hooks/use-online"
import {
  intersectRejectionReasonCodes,
  useRejectionReasonOptions,
} from "@/hooks/use-rejection-reason-options"
import { cn } from "@/lib/utils"

/**
 * OKS-19 — Bulk reject confirmation modal (UX Spec §Flows step 3 +
 * §Responsive). Shadcn `Drawer` on `<768px`, `Dialog` on `≥768px` —
 * matches the OKS-18 RejectConfirmation pattern that ships in the
 * existing `CandidateActionBar` (port-adapt).
 *
 * **Inertia `<Form>` — Crew rule.** No shadcn Form, no react-hook-form,
 * no zod. Plain shadcn `Textarea` + `Label` with locked nested
 * strong-params `name=` attributes:
 *
 *   - `name="bulk_transition[event]"` (hidden, value="reject")
 *   - `name="bulk_transition[reason]"` (textarea — OPTIONAL per PRD;
 *     applies the same value to every selected candidate)
 *   - `name="bulk_transition[candidate_ids][]"` (one hidden input per
 *     selected id; Vitest asserts `length === selectedIds.length`)
 *
 * **Wire-format canaries (memory `feedback_vitest_wire_format_canary`):**
 * the Vitest spec asserts the LITERAL `name=` strings, not just
 * attribute presence. BE strong-params signature is
 * `params.require(:bulk_transition).permit(:event, :reason,
 * candidate_ids: [])`; any drift → 400 → silent failure → memory
 * `feedback_playwright_form_canary` exit path. Vitest is the
 * first-line defence; Playwright E5 is the wire-format end-to-end.
 *
 * **No optimistic UI** (UX Spec §Detail action buttons → applies to
 * the whole approval workflow): the BE 303-redirects to `/candidates`
 * (Referer-preserving) and the toast renders from `flash[:success]`
 * (with `bulk_result` payload). FE never simulates the transition
 * locally — server is authoritative.
 *
 * **Locked testids (FE TRD §testid Map — frozen):**
 *   - `candidate-list-bulk-confirm-dialog` — Dialog/Drawer root
 *   - `candidate-list-bulk-reject-reason` — optional reason textarea
 *   - `candidate-list-bulk-confirm` — destructive submit
 *   - `candidate-list-bulk-cancel` — ghost cancel
 *
 * **A11y (UX Spec §A11y):**
 *   - Modal is focus-trapped (shadcn Dialog/Drawer handles this).
 *   - ESC closes; focus restores to the bulk bar's Reject trigger
 *     (parent owns trigger ref — controlled `open` + `onOpenChange`).
 *   - `<Label htmlFor>` linked to the textarea.
 *   - Reason character counter is `aria-live="polite"` only when
 *     ≥240 chars (UX Spec — avoid SR spam).
 */
export interface BulkRejectConfirmationProps {
  /** Controlled `open` state — parent (Index.tsx F6) owns the modal lifecycle. */
  open: boolean
  /** Callback fired by Dialog/Drawer when user closes via ESC / overlay / cancel. */
  onOpenChange: (open: boolean) => void
  /** BE-supplied bulk transition endpoint URL — `bulk.transition_url` prop. */
  actionUrl: string
  /** Snapshot of selected candidate ids at the moment the modal opens. */
  selectedIds: readonly number[]
  /**
   * **OKS-952** — distinct `from_status` values across the selection.
   *
   * The reason list is narrowed to codes legal for EVERY selected row. When
   * nothing substantive is shared, the picker is replaced by a notice and the
   * batch can still be refused with a note alone — which is exactly the shipped
   * pre-OKS-952 behaviour, so the surface never dead-ends.
   *
   * ⚠️ The trigger is `intersection − {other}` being empty, NOT a gate
   * comparison. Two statuses in different gates can still share codes
   * (`reviewed` + `pending_brand_validation` share four), so a gate test would
   * deny a usable intersection for a purely cosmetic reason — and would change
   * behaviour again the next time the label voices are re-grouped. The server
   * enforces the same rule on the write path and refuses the WHOLE batch.
   */
  selectedRows?: readonly { id: number; status: string }[]
  /**
   * **OKS-952** — narrows the parent's selection to `ids`, backing §3.4's
   * one-click "Keep only the N …" buttons.
   *
   * 🔴 The implementation MUST only ever remove. `use-bulk-selection.retain`
   * does; `idsToDrop.forEach(toggle)` does NOT — `toggle` on an unselected id
   * ADDS it, so that path silently widens a batch and would reject candidates
   * the user never picked, atomically, onto an append-only table.
   */
  onNarrow?: (ids: number[]) => void
  /**
   * Optional finished-callback fired AFTER the Inertia POST resolves
   * successfully (Index.tsx F6 uses this to clear selection on the
   * non-redirect path; though Inertia redirects on bulk per BE locked
   * contract, defensive cleanup hook stays for forward compat).
   */
  onSuccess?: () => void
}

/**
 * Soft cap — UX Spec §Edge Cases: counter `N / 300` muted by default,
 * turns red at ≥300. Submit STILL allowed past 300 (soft limit; hard
 * cap is `MAX_REASON_LENGTH`). Drives both the visible counter color
 * and the optional `aria-live="polite"` announcement when ≥240 chars
 * (UX Spec §A11y — only announce when approaching the limit to avoid
 * SR spam on every keystroke).
 */
const SOFT_REASON_LIMIT = 300

/**
 * OKS-1012 — id of the note-helper `<p>` in `noteFooter`, passed to the picker
 * as `noteDescribedBy`. Derived from this surface's `idPrefix`
 * (`candidate-list-bulk`) so it cannot collide with the detail surface's.
 */
const BULK_REJECT_NOTE_HELP_ID = "candidate-list-bulk-reason-note-help"
const ARIA_ANNOUNCE_THRESHOLD = 240

export function BulkRejectConfirmation({
  open,
  onOpenChange,
  actionUrl,
  selectedIds,
  selectedRows = [],
  onNarrow,
  onSuccess,
}: BulkRejectConfirmationProps) {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const online = useOnline()
  const allOptions = useRejectionReasonOptions()

  // OKS-952 — the batch's coded reason. Controlled here (not by the Inertia
  // <Form>) because the note-required rule and the narrowing both need it in
  // React state; the picker still emits the `name=` attrs, so <Form>
  // serialisation is unaffected.
  const [reasonCode, setReasonCode] = useState("")
  const [note, setNote] = useState("")
  const [noteMissing, setNoteMissing] = useState(false)

  const selectedStatuses = useMemo(
    () => [...new Set(selectedRows.map((row) => row.status))],
    [selectedRows],
  )

  const sharedCodes = useMemo(
    () => intersectRejectionReasonCodes(allOptions, selectedStatuses),
    [allOptions, selectedStatuses],
  )

  // §3.4's narrowing partition. Splits on the SCREENED-OUT vs DECLINED axis —
  // `screening` and `brand_validation` are two voices but one side of the
  // decision (we ruled them out), and that is the axis on which the
  // intersection collapses to `{other}`. Grouping them keeps a
  // reviewed + pending_brand_validation batch narrowable, which it should be:
  // those two share four codes.
  const screenedOutIds = useMemo(
    () =>
      selectedRows
        .filter((row) => allOptions[row.status]?.gate !== "declined")
        .map((row) => row.id),
    [selectedRows, allOptions],
  )
  const declinedIds = useMemo(
    () =>
      selectedRows
        .filter((row) => allOptions[row.status]?.gate === "declined")
        .map((row) => row.id),
    [selectedRows, allOptions],
  )
  const canNarrow =
    Boolean(onNarrow) && screenedOutIds.length > 0 && declinedIds.length > 0

  // `other` alone is not a usable offer: it REQUIRES free text, so presenting
  // it as the only choice forces one note onto rows that mean different
  // things — the corruption the taxonomy exists to prevent.
  const hasSharedReason = sharedCodes.some((code) => code !== "other")

  // Gate is copy only, so the notice's own voice comes from the FIRST selected
  // status that has one. Any of them is honest here: the notice's job is to
  // say "no single reason fits", not to speak in one row's voice.
  const pickerOptions = useMemo(() => {
    if (!hasSharedReason) return undefined
    const firstGate = selectedStatuses
      .map((status) => allOptions[status]?.gate)
      .find(Boolean)
    return firstGate ? { gate: firstGate, codes: sharedCodes } : undefined
  }, [hasSharedReason, selectedStatuses, allOptions, sharedCodes])

  // How many codes the narrowing removed, for the "N reasons hidden" line. Only
  // meaningful when the selection is actually mixed.
  const hiddenCount = useMemo(() => {
    if (!hasSharedReason || selectedStatuses.length < 2) return 0
    const widest = Math.max(
      ...selectedStatuses.map((s) => allOptions[s]?.codes.length ?? 0),
      0,
    )
    return Math.max(widest - sharedCodes.length, 0)
  }, [hasSharedReason, selectedStatuses, allOptions, sharedCodes])

  const count = selectedIds.length

  // UX review Round 1 — char counter for the reason textarea.
  // Tracks length locally so we don't re-render the parent on every
  // keystroke. Resets to 0 each open/close cycle (the textarea is
  // uncontrolled — Inertia <Form> reads .value at submit-time; the
  // counter is purely cosmetic + a11y).
  const [reasonLength, setReasonLength] = useState(0)
  const isOverSoftLimit = reasonLength >= SOFT_REASON_LIMIT
  const shouldAnnounce = reasonLength >= ARIA_ANNOUNCE_THRESHOLD

  const formContent = (
    <Form
      action={actionUrl}
      method="post"
      options={{ preserveScroll: true }}
      // OKS-952 — latency-only guard on the shared `other` rule. Returning
      // false cancels the visit before it leaves the browser. `onBefore` is a
      // top-level Form prop, NOT an `options` key — `FormComponentOptions` is a
      // narrow Pick that excludes every callback. The server re-checks and
      // refuses the WHOLE batch (`:reason_required_for_other`), so a bypassed
      // client still fails closed; this only saves a round-trip.
      onBefore={() => {
        if (isNoteMissing(reasonCode, note)) {
          setNoteMissing(true)
          return false
        }
        setNoteMissing(false)
        return true
      }}
      // Server 303-redirects to /candidates; Inertia follows. The
      // `onSuccess` prop fires after the visit resolves (the new
      // /candidates page has mounted with fresh props). F6 hooks
      // selection-clear here defensively — the hook's
      // `router.on("start")` handler also clears, so this is
      // belt-and-suspenders for explicit awareness.
      onSuccess={() => onSuccess?.()}
    >
      {({ processing }) => (
        <div className="flex flex-col gap-4">
          {/*
            Hidden `bulk_transition[event]` — wire-format canary
            target. Locked literal value="reject" matches BE's
            event closed-set. Vitest + QA Playwright assert via
            `name="bulk_transition[event]"` selector (not testid).
          */}
          <input type="hidden" name="bulk_transition[event]" value="reject" />
          {/*
            One hidden input per selected id under
            `bulk_transition[candidate_ids][]` — Rails-canonical
            multi-value array. Order matches selectedIds iteration
            (insertion order on the underlying Set). Cap=25 enforced
            client-side via the F2 selection hook + BE.
          */}
          {selectedIds.map((id) => (
            <input
              key={id}
              type="hidden"
              name="bulk_transition[candidate_ids][]"
              value={id}
            />
          ))}
          <RejectionReasonPicker
            options={pickerOptions}
            value={reasonCode}
            onValueChange={setReasonCode}
            note={note}
            onNoteChange={(next) => {
              setNote(next)
              setReasonLength(next.length)
            }}
            namePrefix="bulk_transition"
            idPrefix="candidate-list-bulk"
            noteTestId="candidate-list-bulk-reject-reason"
            noteMissing={noteMissing}
            disabled={processing}
            helperSlot={
              hiddenCount > 0 ? (
                <p
                  data-testid="candidate-list-bulk-reject-narrowed"
                  className="text-muted-foreground text-xs"
                >
                  {t("candidates.bulk_reject.narrowed", {
                    count: hiddenCount,
                    selected: count,
                  })}
                </p>
              ) : null
            }
            noteFooter={
              // OKS-1012 — one helper line in EVERY state. Its text swaps on the
              // SAME `isNoteRequired` rule the picker's label reads, so the two
              // cannot drift: « Facultatif — … » would contradict « Note
              // (obligatoire) », and hiding the line (OKS-1003) dropped the
              // visibility disclosure exactly when the note is compelled. The
              // helper is the note's accessible description (`noteDescribedBy`
              // below); the counter is deliberately not part of it.
              <div className="flex items-start justify-between gap-3">
                <p
                  id={BULK_REJECT_NOTE_HELP_ID}
                  data-testid="candidate-list-bulk-reject-reason-helper"
                  className="text-muted-foreground text-xs"
                >
                  {isNoteRequired(reasonCode)
                    ? t("candidates.list.bulk.reject_reason_helper_required")
                    : t("candidates.list.bulk.reject_reason_helper")}
                </p>
                <p
                  data-testid="candidate-list-bulk-reject-reason-counter"
                  className={cn(
                    "shrink-0 text-xs tabular-nums",
                    isOverSoftLimit
                      ? "text-destructive font-medium"
                      : "text-muted-foreground",
                  )}
                  aria-live={shouldAnnounce ? "polite" : "off"}
                >
                  {t("candidates.list.bulk.reject_reason_counter", {
                    count: reasonLength,
                    limit: SOFT_REASON_LIMIT,
                  })}
                </p>
              </div>
            }
            noteDescribedBy={BULK_REJECT_NOTE_HELP_ID}
          />
          {!hasSharedReason && selectedStatuses.length > 1 && (
            <div className="flex flex-col gap-2">
              <p
                data-testid="candidate-list-bulk-reject-no-shared-reason"
                className="text-muted-foreground text-xs"
              >
                {canNarrow
                  ? t("candidates.bulk_reject.mixed_gate.notice", {
                      screening: screenedOutIds.length,
                      declined: declinedIds.length,
                    })
                  : t("candidates.bulk_reject.no_shared_reason")}
              </p>
              {/*
                §3.4 — one-click narrowing. Rendered only when BOTH sides are
                non-empty; with one side empty there is nothing to narrow to
                and the buttons would be a no-op dressed as a fix.

                ⚠️ `onNarrow` must be backed by a remove-only primitive
                (`use-bulk-selection.retain`). A `toggle`-based narrowing
                WIDENS as soon as the argument holds an unselected id.
              */}
              {canNarrow && (
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    data-testid="candidate-list-bulk-reject-keep-screening"
                    disabled={processing}
                    onClick={() => onNarrow?.(screenedOutIds)}
                  >
                    {t("candidates.bulk_reject.mixed_gate.keep_screening", {
                      count: screenedOutIds.length,
                    })}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    data-testid="candidate-list-bulk-reject-keep-declined"
                    disabled={processing}
                    onClick={() => onNarrow?.(declinedIds)}
                  >
                    {t("candidates.bulk_reject.mixed_gate.keep_declined", {
                      count: declinedIds.length,
                    })}
                  </Button>
                </div>
              )}
            </div>
          )}
          {/*
            Footer is rendered inside Form so the submit button is a
            real form-submit (not a click handler). Two variants —
            DialogFooter / DrawerFooter — keep the visual hierarchy
            consistent with the surrounding shell (Dialog has
            right-aligned footer; Drawer stacks footer items by
            default).
          */}
          {isMobile ? (
            <DrawerFooter className="px-0">
              <Button
                type="submit"
                variant="destructive"
                data-testid="candidate-list-bulk-confirm"
                disabled={processing || !online}
              >
                {t("candidates.list.bulk.confirm_reject", { count })}
              </Button>
              <Button
                type="button"
                variant="ghost"
                data-testid="candidate-list-bulk-cancel"
                disabled={processing}
                onClick={() => onOpenChange(false)}
              >
                {t("candidates.list.bulk.cancel")}
              </Button>
            </DrawerFooter>
          ) : (
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                data-testid="candidate-list-bulk-cancel"
                disabled={processing}
                onClick={() => onOpenChange(false)}
              >
                {t("candidates.list.bulk.cancel")}
              </Button>
              <Button
                type="submit"
                variant="destructive"
                data-testid="candidate-list-bulk-confirm"
                disabled={processing || !online}
              >
                {t("candidates.list.bulk.confirm_reject", { count })}
              </Button>
            </DialogFooter>
          )}
        </div>
      )}
    </Form>
  )

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent data-testid="candidate-list-bulk-confirm-dialog">
          <DrawerHeader>
            <DrawerTitle>
              {t("candidates.list.bulk.reject_title", { count })}
            </DrawerTitle>
            <DrawerDescription>
              {t("candidates.list.bulk.reject_description", { count })}
            </DrawerDescription>
          </DrawerHeader>
          <div className="px-4 pb-4">{formContent}</div>
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="candidate-list-bulk-confirm-dialog"
        className="sm:max-w-[560px]"
      >
        <DialogHeader>
          <DialogTitle>
            {t("candidates.list.bulk.reject_title", { count })}
          </DialogTitle>
          <DialogDescription>
            {t("candidates.list.bulk.reject_description", { count })}
          </DialogDescription>
        </DialogHeader>
        {formContent}
      </DialogContent>
    </Dialog>
  )
}
